import SEO from "../seo/SEO";
import { HelpCircle, Award, Users, Shield } from "lucide-react";

export default function AboutUs() {
  return (
    <div className="space-y-16 py-12">
      <SEO 
        title="About Us - USMoneyAI" 
        description="Learn about USMoneyAI, the premiere open-source fintech planning library offering 77+ interactive calculators with absolute client-side calculation security."
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "About Us", url: "/about" }
        ]}
      />
      
      <div className="text-center space-y-4 max-w-2xl mx-auto">
        <div className="flex justify-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-cyan-500/25 bg-cyan-950/30 text-xs font-mono text-cyan-400">
            <Users className="w-3.5 h-3.5" />
            About Us
          </div>
        </div>
        <h1 className="text-4xl font-display font-medium text-white tracking-tight sm:text-5xl">
          About USMoneyAI
        </h1>
        <p className="text-slate-400 text-sm font-light leading-relaxed">
          The premiere open-source fintech planning suite offering fast, accessible, and mathematically validated calculations with absolute client-side security.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
        <div className="bg-[#040914] border border-slate-900 rounded-2xl p-6 space-y-4">
          <div className="w-10 h-10 rounded-xl bg-cyan-950/40 border border-cyan-850/50 flex items-center justify-center text-cyan-400">
            <Shield className="w-5 h-5" />
          </div>
          <h3 className="text-lg font-semibold text-white">Privacy-Focused Design</h3>
          <p className="text-xs text-slate-400 font-light leading-relaxed">
            All variables are processed instantly on your personal local browser. We never transmit, record, or track your custom numerical parameters or results.
          </p>
        </div>

        <div className="bg-[#040914] border border-slate-900 rounded-2xl p-6 space-y-4">
          <div className="w-10 h-10 rounded-xl bg-cyan-950/40 border border-cyan-850/50 flex items-center justify-center text-cyan-400">
            <Users className="w-5 h-5" />
          </div>
          <h3 className="text-lg font-semibold text-white">Everyday Accessibility</h3>
          <p className="text-xs text-slate-400 font-light leading-relaxed">
            We provide more than 77 free financial calculators that are available without registration, subscriptions, or hidden fees.
          </p>
        </div>

        <div className="bg-[#040914] border border-slate-900 rounded-2xl p-6 space-y-4">
          <div className="w-10 h-10 rounded-xl bg-cyan-950/40 border border-cyan-850/50 flex items-center justify-center text-cyan-400">
            <HelpCircle className="w-5 h-5" />
          </div>
          <h3 className="text-lg font-semibold text-white">Interactive Guidance</h3>
          <p className="text-xs text-slate-400 font-light leading-relaxed">
            Many calculators include payment schedules, charts, and detailed breakdowns to help users better understand their financial decisions.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto bg-slate-950/20 border border-slate-900/50 rounded-2xl p-8 sm:p-10 space-y-6">
        <h2 className="text-2xl font-semibold text-white tracking-tight border-b border-slate-900 pb-3 text-left">
          Why USMoneyAI Was Created
        </h2>
        <div className="space-y-4 text-slate-300 text-sm font-light leading-relaxed text-left">
          <p>
            USMoneyAI was launched in 2026 to help users quickly calculate loans, mortgages, savings, retirement goals, debt repayment plans, and investment projections without relying on complicated spreadsheets or difficult financial formulas.
          </p>
          <p>
            We noticed that many financial calculators available online were either difficult to use, cluttered with unnecessary information, or designed in a way that made financial planning more confusing than helpful. As a result, many users struggled to find simple tools that could provide clear and understandable results.
          </p>
          <p>
            Our goal is to make financial planning easier by offering free, fast, and accessible calculators that anyone can use. Whether you're estimating a loan payment, planning your retirement, building an emergency fund, or calculating investment growth, our tools are designed to provide straightforward results that help support better financial decisions.
          </p>
          <p>
            At USMoneyAI, we focus on simplicity, usability, and accuracy so that users can spend less time calculating and more time planning for their financial future.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto bg-slate-950/40 border border-slate-800 rounded-2xl p-8 space-y-4 text-center">
        <h2 className="text-xl font-semibold text-white">Our Mission</h2>
        <p className="text-xs sm:text-sm text-slate-300 font-light leading-relaxed">
          Our mission is to provide reliable financial calculators and educational resources that help people make informed decisions about borrowing, saving, investing, and budgeting.
        </p>
      </div>
    </div>
  );
}
